import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface TeacherLineLinkRow {
  id: string;
  code: string;
  name: string;
  nickname: string | null;
  has_line_link: boolean;
  line_display_name: string | null;
}

export function useTeacherLineLinkList() {
  return useQuery({
    queryKey: ['teacher-line-link-list'],
    staleTime: 60_000,
    queryFn: async (): Promise<TeacherLineLinkRow[]> => {
      const client = supabase.schema('teacher' as any) as any;
      const [teachersRes, profilesRes] = await Promise.all([
        client.from('teachers').select('id, code, name, nickname, status'),
        supabase.from('teacher_profiles').select('teacher_code, line_user_id, line_display_name'),
      ]);
      if (teachersRes.error) throw teachersRes.error;
      if (profilesRes.error) throw profilesRes.error;

      const profileMap = new Map<string, { line_user_id: string | null; line_display_name: string | null }>();
      (profilesRes.data || []).forEach((p: any) => {
        profileMap.set(p.teacher_code, { line_user_id: p.line_user_id, line_display_name: p.line_display_name });
      });

      const rows: TeacherLineLinkRow[] = (teachersRes.data || [])
        .filter((t: any) => (t.status || '').toLowerCase() === 'active')
        .map((t: any) => {
          const p = profileMap.get(t.code);
          return {
            id: String(t.id),
            code: t.code || '',
            name: t.name || '',
            nickname: t.nickname || null,
            has_line_link: !!p?.line_user_id,
            line_display_name: p?.line_display_name || null,
          };
        })
        .sort((a, b) => a.code.localeCompare(b.code));

      return rows;
    },
  });
}
